import { Button, CodeInput, Form, MethodCard, Modal, Typography } from '@cannect/new-components/atoms'
import { TModalActions } from '@cannect/new-components/atoms/Modal'
import { useTwoFactorLoginFlow } from '@cannect/hooks/useTwoFactorLoginFlow'
import { MFA_METHOD_DESCRIPTION, MFA_METHOD_ICON, MFA_METHOD_LABEL } from '@cannect/utils/mfaMethodPresentation'
import { LuCopy, LuX } from 'react-icons/lu'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

// Custo de SMS (AWS) inviável — desativado por flag reversível, código
// preservado para reativação futura.
const isMfaSmsEnabled = import.meta.env.VITE_APP_MFA_SMS_ENABLED === 'true'

interface TwoFactorLoginGateProps {
  flow: ReturnType<typeof useTwoFactorLoginFlow>
}

// Modal única para os 3 fluxos gerados pelo login em 2 passos (nota 3 §1/§2/§3
// da feature portal-cannect-2fa): escolha de método, código do desafio, e
// enrollment obrigatório (usuário sem 2FA ou recém-recuperado). Reaproveitado
// por Login/index.tsx e pelos 2 fluxos de auto-login pós-cadastro
// (Registration/paciente, Registration/profissionais) — o mecanismo é
// idêntico nos 3, só o `onAuthenticated` final muda por página.
export const TwoFactorLoginGate = ({ flow }: TwoFactorLoginGateProps) => {
  const { step, loading, errorMessage } = flow
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const modalRef = useRef<TModalActions>(null)

  const isOpen = step.kind !== 'idle'

  // O Modal atom controla `isOpen` internamente (API imperativa via ref) —
  // não aceita um `open` controlado por prop nem um callback de close. Ponte
  // padrão do repo (mesmo mecanismo do ModalConfirm): sincroniza o estado do
  // fluxo pra dentro via ref, e fecha só pelo nosso próprio botão (abaixo),
  // nunca pelo close/outside-click interno do atom.
  useEffect(() => {
    if (isOpen) modalRef.current?.open()
    else modalRef.current?.close()
  }, [isOpen])

  const onCloseModal = () => {
    setCode('')
    setUseRecovery(false)
    flow.reset()
  }

  const submit = (submittedCode: string = code) => {
    if (step.kind === 'challenge') {
      if (useRecovery) flow.submitRecoveryCode(submittedCode)
      else flow.submitCode(submittedCode)
    } else if (step.kind === 'enrollment_confirm') {
      flow.confirmEnrollCode(submittedCode)
    }
    setCode('')
  }

  const copyToClipboard = async (text: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(message)
    } catch {
      toast.error('Não foi possível copiar. Copie manualmente.')
    }
  }

  return (
    <Modal
      ref={modalRef}
      hiddenCloseButton
      classNames={{ containerClassName: 'w-full max-w-[420px] sm:max-w-[480px] rounded-2xl px-4 py-8 sm:px-10' }}>
      <div className="flex flex-col gap-5">
        {step.kind !== 'recovery_codes' && (
          <div className="absolute right-3 top-3">
            <Button unstyled type="button" aria-label="Fechar" onClick={onCloseModal}>
              <LuX className="text-gray-800" size={24} />
            </Button>
          </div>
        )}

        {step.kind === 'method_required' && (
          <>
            <Typography.Heading type="headingFive" weight="medium">
              Escolha como confirmar sua identidade
            </Typography.Heading>
            <div className="flex flex-col gap-3">
              {step.methods
                .filter((method) => isMfaSmsEnabled || method !== 'sms')
                .map((method) => (
                  <MethodCard
                    key={method}
                    icon={MFA_METHOD_ICON[method]}
                    title={MFA_METHOD_LABEL[method]}
                    description={MFA_METHOD_DESCRIPTION[method]}
                    disabled={loading}
                    onClick={() => flow.chooseMethod(method)}
                  />
                ))}
            </div>
          </>
        )}

        {step.kind === 'challenge' && !useRecovery && (
          <>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                {MFA_METHOD_ICON[step.method]}
              </span>
              <Typography.Heading type="headingFive" weight="medium">
                Confirme sua identidade
              </Typography.Heading>
            </div>
            <Typography.Text muted>
              {step.method === 'totp'
                ? 'Abra seu aplicativo autenticador e informe o código de 6 dígitos.'
                : `Enviamos um código de 6 dígitos para ${step.maskedTarget}.`}
            </Typography.Text>
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={submit}
              ariaLabel="Código de verificação de 6 dígitos"
              error={!!errorMessage}
              disabled={loading}
              autoFocus
            />
            {errorMessage && <p className="-mt-2 text-sm text-red-600">{errorMessage}</p>}
            <Button isLoading={loading} full onClick={() => submit()} disabled={code.length !== 6}>
              Confirmar
            </Button>
            <div className="flex justify-between gap-4">
              {step.method !== 'totp' && (
                <Button
                  unstyled
                  type="button"
                  className="text-sm text-gray-500 underline hover:text-gray-700"
                  onClick={() => flow.resendCode()}>
                  Reenviar código
                </Button>
              )}
              <Button
                unstyled
                type="button"
                className="text-sm text-gray-500 underline hover:text-gray-700"
                onClick={() => setUseRecovery(true)}>
                Usar código de recuperação
              </Button>
            </div>
          </>
        )}

        {step.kind === 'challenge' && useRecovery && (
          <>
            <Typography.Heading type="headingFive" weight="medium">
              Usar código de recuperação
            </Typography.Heading>
            <Typography.Text muted>
              Informe um dos códigos de recuperação gerados quando você configurou o 2FA. Isso vai desativar seus outros
              métodos e permitir configurar um novo.
            </Typography.Text>
            <Form.Input
              variant="filledLight"
              label="Código de recuperação"
              value={code}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setCode(event.target.value)}
            />
            {errorMessage && <p className="-mt-2 text-sm text-red-600">{errorMessage}</p>}
            <Button isLoading={loading} full onClick={() => submit()} disabled={!code}>
              Confirmar
            </Button>
            <Button
              unstyled
              type="button"
              className="self-start text-sm text-gray-500 underline hover:text-gray-700"
              onClick={() => setUseRecovery(false)}>
              Voltar
            </Button>
          </>
        )}

        {step.kind === 'enrollment_select' && (
          <>
            <Typography.Heading type="headingFive" weight="medium">
              Configure a autenticação em duas etapas
            </Typography.Heading>
            <Typography.Text muted>Escolha um método para proteger sua conta. Isso é obrigatório.</Typography.Text>
            <div className="flex flex-col gap-3">
              {/* TOTP só é configurável via perfil (TwoFactorSettings), não neste enrolment obrigatório. */}
              {(['email', 'sms'] as const)
                .filter((method) => isMfaSmsEnabled || method !== 'sms')
                .map((method) => (
                  <MethodCard
                    key={method}
                    icon={MFA_METHOD_ICON[method]}
                    title={MFA_METHOD_LABEL[method]}
                    description={MFA_METHOD_DESCRIPTION[method]}
                    disabled={loading}
                    onClick={() => flow.chooseEnrollMethod(method)}
                  />
                ))}
            </div>
            {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
          </>
        )}

        {step.kind === 'enrollment_confirm' && (
          <>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                {MFA_METHOD_ICON[step.method]}
              </span>
              <Typography.Heading type="headingFive" weight="medium">
                Confirme o método
              </Typography.Heading>
            </div>
            {step.method === 'totp' && step.otpauthUri && (
              <>
                <Typography.Text muted>
                  Adicione esta chave no seu aplicativo autenticador (Google Authenticator, Authy, etc.):
                </Typography.Text>
                <button
                  type="button"
                  onClick={() => step.secret && copyToClipboard(step.secret, 'Chave copiada.')}
                  className="group flex appearance-none items-center justify-between gap-2 rounded-md border border-solid border-gray-200 bg-gray-50 px-3 py-2.5 text-left outline-none transition-colors hover:border-primary-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2">
                  <code className="break-all font-mono text-sm text-gray-900">{step.secret}</code>
                  <LuCopy
                    className="shrink-0 text-gray-400 group-hover:text-primary-700"
                    size={16}
                    aria-hidden="true"
                  />
                </button>
              </>
            )}
            {step.method !== 'totp' && (
              <Typography.Text muted>Enviamos um código de 6 dígitos para {step.maskedTarget}.</Typography.Text>
            )}
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={submit}
              ariaLabel="Código de confirmação de 6 dígitos"
              error={!!errorMessage}
              disabled={loading}
              autoFocus
            />
            {errorMessage && <p className="-mt-2 text-sm text-red-600">{errorMessage}</p>}
            <Button isLoading={loading} full onClick={() => submit()} disabled={code.length !== 6}>
              Confirmar
            </Button>
            {step.method !== 'totp' && (
              <Button
                unstyled
                type="button"
                className="self-start text-sm text-gray-500 underline hover:text-gray-700"
                onClick={() => flow.resendEnrollCode()}>
                Reenviar código
              </Button>
            )}
          </>
        )}

        {step.kind === 'recovery_codes' && (
          <>
            <Typography.Heading type="headingFive" weight="medium">
              Guarde seus códigos de recuperação
            </Typography.Heading>
            <Typography.Text muted>
              Use um destes códigos se você perder acesso ao seu método de autenticação. Cada código só pode ser usado
              uma vez e eles não serão mostrados novamente. Toque em um código para copiá-lo.
            </Typography.Text>
            <div className="grid grid-cols-2 gap-2">
              {step.codes.map((recoveryCode) => (
                <button
                  key={recoveryCode}
                  type="button"
                  onClick={() => copyToClipboard(recoveryCode, 'Código copiado.')}
                  className="group flex appearance-none items-center justify-between gap-1.5 rounded-md border border-solid border-gray-200 bg-gray-50 px-3 py-2 text-left outline-none transition-colors hover:border-primary-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2">
                  <code className="break-all font-mono text-sm text-gray-900">{recoveryCode}</code>
                  <LuCopy
                    className="shrink-0 text-gray-400 group-hover:text-primary-700"
                    size={14}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
            <Button isLoading={loading} full onClick={() => flow.finishAfterRecoveryCodes()}>
              Já guardei, continuar
            </Button>
          </>
        )}
      </div>
    </Modal>
  )
}
